import { ListContextProvider, useListContext, useListController, useRecordContext } from "ra-core"
import { FC, useMemo } from "react"
import { Content } from "~/components/content/Content"
import { OdResourcePageProps } from "~/types/resource"
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { Card, Divider, Drawer, Stack, styled, Typography, useMediaQuery, useTheme } from "@mui/material"
import { Box } from "@mui/system"
import { Datagrid } from "~/components/list/datagrid"
import Scrollbar from "~/components/Scrollbar"
import { RecPatch } from "~/dataProvider/tools"
import { DataCatalogFilters } from "~/components/governance/DataCatalogFilters"
import { FunctionField } from "~/components/field"
import ClassificationStatistics from "~/components/governance/ClassificationStatistics"
import ExportDataCatalogButton from "~/components/governance/ExportDataCatalogButton"
import { DataCatalogContextProvider, useDataCatalogContext } from "~/components/governance/dataCatalogContext/DataCatalogContext"
import { useResourceRegister } from "~/utils/resourceRegister"
import { DataCatalogResponseDto, ExtraIgnoreItem } from "~/gen/api/governance"
import EncryptStatusStatistics from "~/components/governance/EncryptStatusStatistics"
import { AmbientFacilitiesContextProvider, useAmbientFacilitiesContext } from "~/components/governance/search/AmbientFacilitiesContext"
import RowExpandFieldEdit from "~/components/governance/fieldEdit/RowExpandFieldEdit"
import PopoutFieldBulkEdit from "~/components/governance/fieldEdit/PopoutFieldBulkEdit"
import FieldRowLabels from "~/components/governance/labels/FieldRowLabels"
import PagerMask from "~/components/governance/fieldEdit/PagerMask"
import { dataTypeDisplayInfo } from "~/components/governance/fieldEdit/maskingRuleDataTypesMap"
import { PublicFieldProps } from "~/components/field/types"
import { EncryptedDatabasesContextProvider } from "~/components/databases/useEncryptedDatabases"
import { MANAGE_DATA_CATALOG } from "~/constants"
import { useHasPermit } from "~/authProvider/useHasPermit"



const DataCatalogCard = styled(Card)(({theme}) => ({

  '&.faded': {
    '& table.RaDatagrid-table, & span.MuiTablePagination-root > div': {
      backgroundColor: theme.palette.background.default,
      filter: 'blur(1px)',
    },
  },
}))

const TabularBox = styled(Box)(({theme}) => ({
  minWidth: 1150, 
  position: 'relative',
}))

const DataCatalog: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: 'govrnc-databases' })
  useResourceRegister({ name: 'govrnc-schemas' })
  useResourceRegister({ name: 'govrnc-tables' })
  useResourceRegister({ name: 'govrnc-fields' })
  useResourceRegister({ name: 'govrnc-tags' })
  useResourceRegister({ name: 'govrnc-select-all-fields' })
  useResourceRegister({ name: 'govrnc-encrypt-rule-mapping' })
  useResourceRegister({ name: 'govrnc-vali-bulk' })
  useResourceRegister({ name: 'govrnc-catalog-export' })

  const listCtrl = useListController({ basePath: '' })

  return (
    <ListContextProvider value={listCtrl}>
      <DataCatalogCater {...props} />
    </ListContextProvider>
  )
}

/***
 * Explain fieldEdit ui - context hierarchy:
 * 
 * <DataCatalogContextProvider - DataCatalogContext[ useBulkEdit, statistics, datacatalog list criterias ]>
 *     <-- useBulkEdit[ toggleSelect, selectedIds, viable encryptRules for selected ] -->
 *     <AmbientFacilitiesContextProvider - AmbientFacilitiesContext[ list options data ]>
 *         <filters.etc. />
 *         <EncryptedDatabasesContextProvider - [encrypted dbs (esc locked or not)]>
 *             <PopoutFieldBulkEdit>
 *                 <FieldBulkEditContextProvider - FieldEditContext[ override save:saveBulk ]>
 *                     <FieldEdit />
 *                 </FieldBulkEditContextProvider>
 *             </PopoutFieldBulkEdit>
 *             <RowExpandFieldEdit>
 *                 <FieldRecordEditContextProvider - FieldEditContext[ override save:saveField ]>
 *                     <FieldEdit />
 *                 </FieldRecordEditContextProvider>
 *             </RowExpandFieldEdit>
 *         </EncryptedDatabasesContextProvider>
 *     </AmbientFacilitiesContextProvider>
 * </DataCatalogContextProvider>
 */
export const DataCatalogCater: FC<OdResourcePageProps> = (props) => {
  return (
    <DataCatalogContextProvider>
      <AmbientFacilitiesContextProvider>
        <DataCatalogBody {...props} />
      </AmbientFacilitiesContextProvider>
    </DataCatalogContextProvider>
  )  
}


export const DataCatalogBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasDCWritePermit = useHasPermit([MANAGE_DATA_CATALOG])
  
  const listCtx = useListContext<DataCatalogResponseDto & RecPatch>()
  const dataCatalogCtx = useDataCatalogContext()
  const { 
    tableRef,
    onToggleItem, 
    isRowExpandable, 
    bulkEditOpen, 
    maintenancePageComponent, 
    closeMaintenancePage,
  } = dataCatalogCtx
  const { classifications } = useAmbientFacilitiesContext()
  
  const match = useMediaQuery('(max-width: 1250px)')
  const width = match ? 'calc(100vw - 50px)' : 1200
  const theme = useTheme()
  
  return (
    <Content pageName="Data Catalog" resourceName="Data Catalog" breadcrumbs={options?.breadcrumbs}>
      <Stack direction="row" alignItems="baseline" spacing={8}>
        <ClassificationStatistics />
        <EncryptStatusStatistics />
        {hasDCWritePermit && <ExportDataCatalogButton />}
      </Stack>
      <DataCatalogCard className={bulkEditOpen ? "faded" : ''}>
        <DataCatalogFilters />
        <Divider />
        <Scrollbar>
          <TabularBox>
            <EncryptedDatabasesContextProvider>
              {hasDCWritePermit && <PopoutFieldBulkEdit />}
              <Datagrid 
                ref={tableRef}
                hasBulkActions={hasDCWritePermit} 
                sx={{ 
                  '& th' : { py: 1 },
                  '& td' : { py: 2 },
                  '& tr.RaDatagrid-row.OdHighlighted': {
                    bgcolor: theme.palette.action.hover,
                  },
                  '& tr.RaDatagrid-row.OdFading': {
                    transition: 'background-color 2s',
                  },
                }}
                expand={<RowExpandFieldEdit />}
                {...listCtx}
                onToggleItem={onToggleItem as any}
                isRowExpandable={isRowExpandable}
              >
                <FunctionField 
                  source="" 
                  label="FIELD NAME" 
                  sortable={false} 
                  render={(d?:DataCatalogResponseDto&RecPatch) => (
                    d ?
                    <Stack direction="row" alignItems="center">
                      <Box>
                        <Typography component="p" variant="caption" color="InactiveCaptionText">
                          <span>{d.databaseName}.</span>{" "}
                          <span>{d.schemaName}.</span>
                        </Typography>
                        <Typography component="p" variant="body2">
                          <Typography component="span" variant="body2" color="InactiveCaptionText">{d.tableName}.</Typography>{" "}
                          <Typography component="b" variant="body2" sx={{fontWeight:500}}>{d.name}</Typography>
                        </Typography>
                      </Box>
                      {d.alias &&
                        <Typography component="p" variant="caption" sx={{ pl: 4 }}>
                          <Typography component="span" variant="caption" color="InactiveCaptionText">aka.</Typography>{" "}
                          <Typography component="span" variant="caption">{d.alias}</Typography>
                        </Typography>
                      }
                    </Stack> : null
                  )}
                />
                <FunctionField 
                  source="key" 
                  label="KEY" 
                  sortable={false} 
                  render={(d?:DataCatalogResponseDto&RecPatch) => (
                    d && 
                    <>
                      {d.isPrimaryKey && <Typography variant="body1" >PK</Typography>}
                      {d.isForeignKey &&
                        <Box sx={{ mb: -1 }}>
                          <Typography variant="body1" >FK</Typography> 
                          <Typography component="p" variant="body2" sx={{ transform: 'scale(0.7) translate(-24px, -8px)' }}>
                            <Typography component="span" variant="body2" color="InactiveCaptionText">{d.referencedTableName}.</Typography>{" "}
                            <Typography component="span" variant="body2">{d.referencedFieldName}</Typography>
                          </Typography>
                        </Box>}
                    </>)
                  }
                />
                <DataTypeField 
                  source="dataType" 
                  label="TYPE/LENGTH" 
                  sortable={false} 
                />
                <FunctionField 
                  label={<Box sx={{width: 560}}></Box>} 
                  sortable={false} 
                  render={(d?:DataCatalogResponseDto&RecPatch) => (
                    <FieldRowLabels classifications={classifications} />
                  )}
                />
              </Datagrid>
            </EncryptedDatabasesContextProvider>
          </TabularBox>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
        <PagerMask />
      </DataCatalogCard>
      <Drawer
        anchor="right"
        onClose={closeMaintenancePage}
        open={maintenancePageComponent !== null}
        sx={{ zIndex: theme.zIndex.appBar - 5 }}
        PaperProps={{
          sx: {
            p: 2,
            width: width,
            bgcolor: 'background.default',
            backgroundImage: 'none',
          },          
        }}
      >
        <Box sx={{ pt: 6 }}>
          {maintenancePageComponent}
        </Box>
      </Drawer>
    </Content>
  )
}


export default DataCatalog;

const DataTypeField: FC<PublicFieldProps> = () => {
  const d = useRecordContext() as DataCatalogResponseDto;
  
  return useMemo(() => {
    if (!d) { return null; }

    const info = dataTypeDisplayInfo({...d, dataType: d.type})
    return (
      <Typography
          component="span"
          variant="body2"
      >
        {(d.isIdentity || d.isComputed || d.extraIgnoreItem !== ExtraIgnoreItem.None) && <Box>
          {d.isIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity]</Typography>}
          {d.isComputed && <Typography variant="caption" color="InactiveCaptionText">[Computed]</Typography>}
          {d.extraIgnoreItem === ExtraIgnoreItem.TransitiveIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity - transitive]</Typography>}
          <br />
        </Box>}
        {info.dataType}{info.dataTypeExt}
      </Typography>
    )
  }, [d]);
}
